<!--
  Toast Component
  Individual toast notification.
-->
<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { toastStore, type Toast } from '../../stores/toastStore';

  export let toast: Toast;

  const typeClasses: Record<string, string> = {
    success: 'border-success/40 text-success',
    error: 'border-error/40 text-error',
    warning: 'border-warning/40 text-warning',
    info: 'border-info/40 text-info',
  };

  const icons: Record<string, string> = {
    success: 'M20 6 9 17l-5-5',
    error: 'M18 6 6 18M6 6l12 12',
    warning: 'M12 9v4m0 4h.01M12 2L2 22h20L12 2z',
    info: 'M12 16v-4m0-4h.01M22 12A10 10 0 1 1 2 12a10 10 0 0 1 20 0z',
  };

  // Hover and focus both pause; the timer resumes only when neither is left.
  let hovered = false;
  let focused = false;
  function sync(): void {
    if (hovered || focused) toastStore.pause(toast.id);
    else toastStore.resume(toast.id);
  }
  // Touch devices fire mouseenter on tap without a matching mouseleave; ignore hover there.
  // jsdom has no matchMedia: treated as hover-capable.
  const canHover = (): boolean =>
    typeof window.matchMedia !== 'function' || window.matchMedia('(hover: hover)').matches;
  function pause(e: Event): void {
    if (e.type === 'mouseenter') {
      if (!canHover()) return;
      hovered = true;
    } else focused = true;
    sync();
  }
  function resume(e: Event): void {
    if (e.type === 'mouseleave') hovered = false;
    else focused = false;
    sync();
  }

  let actionUsed = false;

  function runAction(): void {
    if (actionUsed) return;
    actionUsed = true;
    toastStore.remove(toast.id);
    toast.action?.onClick();
  }
</script>

<div
  class="flex items-start gap-3 px-4 py-3 bg-surface border rounded-lg shadow-card animate-slide-in {typeClasses[
    toast.type
  ]}"
  role={toast.type === 'error' ? 'alert' : 'status'}
  on:mouseenter={pause}
  on:mouseleave={resume}
  on:focusin={pause}
  on:focusout={resume}
>
  <svg
    class="w-5 h-5 shrink-0 mt-0.5"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="2"
  >
    <path d={icons[toast.type]} />
  </svg>

  <span class="flex-1 text-sm text-text">{toast.message}</span>

  {#if toast.action}
    <button
      type="button"
      data-testid="toast-action"
      aria-keyshortcuts="z"
      title="{toast.action.label} (Z)"
      class="shrink-0 px-2 py-1 -my-1 -mr-1 rounded text-sm font-medium text-accent hover:underline bg-transparent border-none cursor-pointer"
      on:click={runAction}
    >
      {toast.action.label}
    </button>
  {/if}

  <button
    type="button"
    class="p-1 -m-1 text-text-muted hover:text-text bg-transparent border-none cursor-pointer transition-colors"
    on:click={() => toastStore.remove(toast.id)}
    aria-label={$_('messages.close')}
  >
    <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  </button>
</div>
